import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import { Alert } from './Alert'
import { cn, focusRing } from './cn'
import { X } from './icons'

export interface ToastProps {
  readonly isOpen: boolean
  readonly onDismiss: () => void
  readonly severity?: 'success' | 'info'
  /** How long it stays before it dismisses itself; the time stops while it is hovered or focused. */
  readonly durationMs?: number
  readonly children: ReactNode
}

/**
 * A short message at the window's bottom right, over the page: the mockups' Alert on a solid Paper
 * surface with a shadow, so it reads over whatever is behind it in both schemes. It renders at the
 * end of `body`, so an ancestor's backdrop-filter or transform cannot move it. Its status region stays
 * in place while closed, so the message is announced when it appears, and it never takes focus.
 */
export function Toast({ isOpen, onDismiss, severity = 'success', durationMs = 8000, children }: ToastProps) {
  const intl = useIntl()
  const [isHovered, setHovered] = useState(false)
  const [isFocused, setFocused] = useState(false)
  const enter = useCallback(() => setHovered(true), [])
  const leave = useCallback(() => setHovered(false), [])
  const focus = useCallback(() => setFocused(true), [])
  const blur = useCallback(() => setFocused(false), [])
  // Closed while hovered or focused, it gets no leave or blur, so the next one starts unheld.
  if (!isOpen && (isHovered || isFocused)) {
    setHovered(false)
    setFocused(false)
  }
  const isHeld = isHovered || isFocused

  useEffect(() => {
    if (!isOpen || isHeld) {
      return undefined
    }
    const timer = window.setTimeout(onDismiss, durationMs)
    return () => window.clearTimeout(timer)
  }, [isOpen, isHeld, durationMs, onDismiss])

  return createPortal(
    // <output> carries role="status" semantics without ARIA.
    <output className="pointer-events-none fixed bottom-4 left-4 right-4 z-50 flex justify-end">
      {isOpen && (
        <div
          onMouseEnter={enter}
          onMouseLeave={leave}
          onFocus={focus}
          onBlur={blur}
          className="pointer-events-auto relative w-full max-w-sm rounded bg-background shadow-card-hover"
        >
          {/* The status region above announces it, so the Alert is not a second live region. */}
          <Alert severity={severity} role="none" className="pr-12">
            {children}
          </Alert>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={intl.formatMessage({ id: 'ui.toast.close' })}
            className={cn(
              'absolute right-2 top-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded text-text-secondary transition hover:text-text-primary',
              focusRing,
            )}
          >
            <X size={18} />
          </button>
        </div>
      )}
    </output>,
    document.body,
  )
}
